
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import HeaderTitle from '@/components/HeaderTitle.vue';
import Installing from '@/components/Installing.vue';
import { StoreModel } from '@/models/store';
import { BillingPackageModel } from '@/models/billing_package';
import { UserPackageModel } from '@/models/user_package';
import { BillingPackageList } from '@/collections/billing_packages';
import { isEmbedded, redirectExternal, redirectApp } from '@/utils/shopifyBridge';
import { EventHandler } from '@/modules/events';
import { EventBus } from '@/main';
import FeatureComparisonTable from '@/components/FeatureComparisonTable.vue';
import ROICalculator from '@/components/pricing/ROICalculator.vue';
import SocialProof from '@/components/pricing/SocialProof.vue';
import FreePlanInterceptionDialog from '@/components/pricing/FreePlanInterceptionDialog.vue';
import {
  getFirstMonthDiscountForPlan,
  fetchDiscount,
} from '@/services/discountService';
import { PricingPageEventHandler } from '@/modules/pricingPageEvents';
import {
  ActiveDiscount,
  calculateDisplayPrice,
  discountAppliesTo,
  discountLabel as discountLabelFn,
  saveAmount,
} from '@/services/pricingDisplay';
import {
  CatalogFeature,
  descriptionFeatures,
  FREE_PLAN_SUMMARY,
  getCatalogEntry,
} from '@/config/onboardPricingCatalog';
import { PROOF_STATS } from '@/config/socialProofStats';

@Component({
  components: {
    HeaderTitle,
    Installing,
    FeatureComparisonTable,
    ROICalculator,
    SocialProof,
    FreePlanInterceptionDialog,
  },
})
export default class OnboardPricing extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  @Prop({ default: () => [] })
  public timeRangeLimit!: string[];
  @Prop({ default: false })
  public isAthenaFinished!: boolean;
  @Prop({ default: null })
  public app!: any;
  public success: boolean = false;
  public error: boolean = false;
  public loading: boolean = false;
  public billingPackages: BillingPackageList = new BillingPackageList();
  public filteredBillingPackages: BillingPackageList = new BillingPackageList();
  public newUserPackage: UserPackageModel = new UserPackageModel();
  public freeBillingPackage: BillingPackageModel = new BillingPackageModel();
  public paidBillingPackage: BillingPackageModel = new BillingPackageModel();
  public paidBillingPackageList: BillingPackageList = new BillingPackageList();
  public backupUserPackage: UserPackageModel = new UserPackageModel();
  public submitting: boolean = false;
  public submitError: string = '';
  public currentDiscount: any = null;

  public offerMessage: string = 'Limited Time: Get up to 60% OFF your first month of Replenishment Center!';

  // Free plan interception dialog visibility
  public showInterceptionDialog: boolean = false;

  // Analytics tracker for pricing page events
  private pricingTracker: PricingPageEventHandler | null = null;
  public async created() {
    localStorage.setItem('onboard_page', '/onboard-pricing');
    this.loading = true;

    // Initialize pricing analytics tracker
    this.pricingTracker = new PricingPageEventHandler();

    try {
      await this.billingPackages.fetch();
      await this.getBillingPackage();
      this.error = false;
      this.success = true;
    } catch (e: any) {
      this.error = true;
      this.success = false;
    }
    this.loading = false;

    // Track pricing page view after data is loaded (non-blocking)
    try {
      if (this.pricingTracker && !this.loading && !this.error) {
        this.pricingTracker.trackPageViewed({
          referrer: document.referrer || 'direct',
          hasActiveDiscount: !!this.currentDiscount,
          discountCode: this.currentDiscount?.code,
        });
      }
    } catch (trackingError) {
      // Silently fail - don't let analytics break the page
      console.warn('[OnboardPricing] Analytics tracking failed:', trackingError);
    }
  }

  /**
   * Watch for discount banner visibility
   * Tracks when discount becomes available and banner is shown
   */
  @Watch('currentDiscount')
  public onDiscountLoaded(newDiscount: any): void {
    // Track discount banner viewed when discount is loaded (non-blocking)
    try {
      if (this.pricingTracker && newDiscount) {
        this.pricingTracker.trackDiscountBannerViewed({
          discountCode: newDiscount.code,
          discountType: newDiscount.type,
          discountValue: newDiscount.rate || newDiscount.discountAmount || 0,
          appliesTo: newDiscount.billingPackageCode || 'all',
          isFirstMonthOnly: true,
        });
      }
    } catch (trackingError) {
      // Silently fail - don't let analytics break page load
      console.warn('[OnboardPricing] Banner view tracking failed:', trackingError);
    }
  }

  /**
   * Track session abandonment on component destruction
   */
  public beforeDestroy(): void {
    // Track session abandonment if user didn't complete subscription (non-blocking)
    try {
      if (this.pricingTracker && !this.submitting) {
        this.pricingTracker.trackSessionAbandoned('navigation');
      }
    } catch (trackingError) {
      // Silently fail - never block navigation
      console.warn('[OnboardPricing] Session abandonment tracking failed:', trackingError);
    }
  }

  public displayPrice(item: BillingPackageModel): number {
    return calculateDisplayPrice(item.price, this.currentDiscount as ActiveDiscount | null, (item as any).code);
  }

  public displaySave(item: BillingPackageModel): number {
    return saveAmount(item.price, this.currentDiscount as ActiveDiscount | null, (item as any).code);
  }

  public hasDiscountFor(item: BillingPackageModel): boolean {
    return item.price > 0 && this.displaySave(item) > 0;
  }

  public cardTagline(item: BillingPackageModel): string {
    const entry = getCatalogEntry((item as any).code);
    return entry ? entry.tagline : (item.description ? item.description.split('\n')[0] : '');
  }

  public cardIntroLine(item: BillingPackageModel): string {
    const entry = getCatalogEntry((item as any).code);
    return entry ? entry.introLine : '';
  }

  public cardFeatures(item: BillingPackageModel): CatalogFeature[] {
    const entry = getCatalogEntry((item as any).code);
    return entry ? entry.features : descriptionFeatures(item.description);
  }

  /** Cards render cheapest→priciest so the premium card lands in the wider right column (mockup parity). */
  get displayPackages(): BillingPackageModel[] {
    return [...this.filteredBillingPackages.items].sort((a, b) => a.price - b.price);
  }

  get freePlanSummary(): string {
    const entry = this.freeBillingPackage && this.freeBillingPackage.description
      ? descriptionFeatures(this.freeBillingPackage.description).slice(0, 3).map((f) => f.text).join(' · ')
      : '';
    return entry || FREE_PLAN_SUMMARY;
  }

  /** Hero proof line — numbers come from PROOF_STATS so they can't drift (DC-7). */
  get proofLine(): string {
    return this.$t('subscription.onboard_proof_line', {
      rating: PROOF_STATS.rating,
      reviews: PROOF_STATS.reviews,
    }) as string;
  }

  public async getBillingPackage() {
    this.paidBillingPackageList = new BillingPackageList();
    this.filteredBillingPackages = new BillingPackageList();
    let discount: any;

    // Check for discount code from URL query param (?discount=XXX)
    const urlDiscountCode = (this.$route.query.discount || this.$route.query.code) as string;
    if (urlDiscountCode) {
      try {
        discount = await fetchDiscount(urlDiscountCode);
        if (discount) {
          this.currentDiscount = discount;
          this.newUserPackage.discountCode = urlDiscountCode;
          this.offerMessage = discount.description || this.offerMessage;
        }
      } catch {
        // Ignore — fall through to plan-based discount
      }
    }

    // Fetch first-month discount for current store's plan (if no URL discount)
    if (!discount) try {
      if (this.store && this.store.assistyPlan) {
        discount = await getFirstMonthDiscountForPlan(this.store.assistyPlan);
        if (discount) {
          this.currentDiscount = discount;
          this.offerMessage = this.currentDiscount.description;
        } else {
          this.currentDiscount = null;
        }
      } else {
        this.currentDiscount = null;
        console.warn('[OnboardPricing] Store or assistyPlan not available');
      }
    } catch (e) {
      console.error('[OnboardPricing] Discount fetch failed:', e);
      this.currentDiscount = null;
    }
    
    const assistyPlan = this.store.assistyPlan;
    for (const item of this.billingPackages.items) {
      if (item.price === 0) {
        this.freeBillingPackage = item;
      } else if (assistyPlan === item.shopifyPlanName) {
        this.paidBillingPackageList.add(item);
      }
    }
    
    // If no packages were found with matching shopifyPlanName, include all paid packages
    if (this.paidBillingPackageList.items.length === 0) {
      for (const item of this.billingPackages.items) {
        if (item.price > 0) {
          this.paidBillingPackageList.add(item);
        }
      }
    }
    
    // sort the paid billing packages by price in descending order
    this.paidBillingPackageList.items.sort((a, b) => b.price - a.price);
    
    this.filteredBillingPackages = new BillingPackageList();

    for (const item of this.paidBillingPackageList.items) {
      this.filteredBillingPackages.add(item);
    }
  }

  public getTranslatedLabel(module: string, text: string) {
    if (this.$te(`${module}.${text}`)) {
      return this.$t(`${module}.${text}`);
    } else {
      return text;
    }
  }

  public async submitPackage(
    item: BillingPackageModel,
    trialDays: number = 14,
  ) {
    this.submitting = true;
    this.newUserPackage.billingPackageId = item.id;
    this.submitError = '';

    // Track subscribe button click (non-blocking, fail silently)
    try {
      if (this.pricingTracker) {
        const packageData = {
          packageCode: (item as any).code || '',
          packageName: item.name,
          price: item.price,
          isYearly: false,
        };

        const discountData = this.currentDiscount
          ? {
              discountCode: this.currentDiscount.code,
              discountType: this.currentDiscount.type,
              discountValue: this.currentDiscount.rate || this.currentDiscount.discountAmount || 0,
              appliesTo: this.currentDiscount.billingPackageCode || 'all',
              isFirstMonthOnly: true,
            }
          : undefined;

        this.pricingTracker.trackSubscribeButtonClicked(packageData, discountData);
      }
    } catch (trackingError) {
      // Silently fail - never let analytics break subscription flow
      console.warn('[OnboardPricing] Subscribe tracking failed:', trackingError);
    }

    // Apply first-month discount code only to paid packages it applies to;
    // clear any code left by a previous attempt so it can't ride along on a
    // non-matching or free package (reachable since failed submits now retry).
    if (item.price > 0 && this.currentDiscount && discountAppliesTo((item as any).code, this.currentDiscount)) {
      this.newUserPackage.discountCode = this.currentDiscount.code;
    } else {
      this.newUserPackage.discountCode = '';
    }

    try {
      let returnUrl = `${process.env.VUE_APP_SUBSCRIPTION_RETURN_URL}`;
      if (isEmbedded()) {
        returnUrl = `https://admin.shopify.com/store/${this.store.shopName.split('.')[0]}/apps/${process.env.VUE_APP_NAME}/subscription/confirm`;
      }
      const confirmationUrl = await this.newUserPackage.create(
        trialDays,
        0,
        returnUrl,
        [],
        false,
      );

      if (this.newUserPackage.status === 'CONFIRMING' && confirmationUrl) {
        try {
          const eventHandler = new EventHandler({
            store: this.store,
          });
          await eventHandler.track('ONBOARDING_CONFIRMING_PAID_PACKAGE', {
            billing_package_id: this.newUserPackage.billingPackageId,
          });
          await this.store.sendEvent('ONBOARDING_CONFIRMING_PAID_PACKAGE', {
            billing_package_id: this.newUserPackage.billingPackageId,
          });
        } catch (e) {
          // ignore this
        }

        // Track subscription creation in analytics (non-blocking)
        try {
          if (this.pricingTracker) {
            const finalPrice = this.displayPrice(item);
            this.pricingTracker.trackSubscriptionCreated({
              packageCode: (item as any).code || '',
              packageName: item.name,
              price: item.price,
              isYearly: false,
              discountApplied: !!this.newUserPackage.discountCode,
              discountCode: this.newUserPackage.discountCode,
              finalPrice: finalPrice,
              subscriptionId: this.newUserPackage.id?.toString() || 'pending',
            });
          }
        } catch (trackingError) {
          // Silently fail - never block subscription flow
          console.warn('[OnboardPricing] Subscription creation tracking failed:', trackingError);
        }

        // Track payment redirect to Shopify (non-blocking)
        try {
          if (this.pricingTracker && confirmationUrl) {
            const finalPrice = this.displayPrice(item);
            this.pricingTracker.trackPaymentRedirect({
              packageCode: (item as any).code || '',
              packageName: item.name,
              price: item.price,
              isYearly: false,
              discountApplied: !!this.newUserPackage.discountCode,
              discountCode: this.newUserPackage.discountCode,
              finalPrice: finalPrice,
              subscriptionId: this.newUserPackage.id?.toString() || 'pending',
            }, confirmationUrl);
          }
        } catch (trackingError) {
          // Silently fail - never block redirect to Shopify
          console.warn('[OnboardPricing] Payment redirect tracking failed:', trackingError);
        }

        redirectExternal(confirmationUrl);
      } else {
        await this.store.updateInstallationStatus();
        try {
          const eventHandler = new EventHandler({
            store: this.store,
          });
          await eventHandler.track('ONBOARDING_START_FREE_PACKAGE', {
            billing_package_id: this.newUserPackage.billingPackageId,
          });
          await this.store.sendEvent('ONBOARDING_START_FREE_PACKAGE', {
            billing_package_id: this.newUserPackage.billingPackageId,
          });
        } catch (e) {
          // ignore this
        }

        // Track free package subscription in analytics (non-blocking)
        try {
          if (this.pricingTracker) {
            this.pricingTracker.trackSubscriptionCreated({
              packageCode: (item as any).code || '',
              packageName: item.name,
              price: item.price,
              isYearly: false,
              discountApplied: false,
              finalPrice: 0,
              subscriptionId: this.newUserPackage.id?.toString() || 'free',
            });
          }
        } catch (trackingError) {
          // Silently fail - never block subscription flow
          console.warn('[OnboardPricing] Free package tracking failed:', trackingError);
        }

        EventBus.$emit('check-athena-finish-interval');
        // Free-plan merchants land on the same onboarding rail as paid ones
        // (SubscriptionConfirm.vue) — /data-importing is a terminal screen, and
        // under managed install this redirect is the merchant's only scheduled
        // visit to the rail: every later app open enters at '/' and Home.vue
        // sends v3 stores to /assistant/today instead.
        // Full reload, not $router.push: the free user_package was created
        // moments ago, so store.currentPackage is stale and Atlas would boot
        // with the pre-subscription plan context.
        redirectApp('/assistant/onboarding');
      }
    } catch (e) {
      this.submitError = this.$t('subscription.onboard_submit_error') as string;
      this.submitting = false;
      return;
    }
  }

  /**
   * Check if a package is the premium plan (highest-priced)
   *
   * Identifies the most expensive package to display "BEST VALUE" badge
   * (when discount badges are not active). Used for UI highlighting.
   *
   * @param {BillingPackageModel} item - Package to check
   * @returns {boolean} True if this is the highest-priced package
   *
   * @remarks
   * - Compares against all packages in filteredBillingPackages
   * - Only returns true for paid packages (price > 0)
   * - If multiple packages have same highest price, all return true
   */
  public isPremiumPlan(item: BillingPackageModel): boolean {
    // Find the highest priced package in the list
    const highestPrice = Math.max(...this.filteredBillingPackages.items.map(pkg => pkg.price));
    // Return true if this item has the highest price
    return item.price === highestPrice && highestPrice > 0;
  }

  public async submitFreePackage() {
    // Show interception dialog instead of directly submitting
    this.showFreePlanInterceptionDialog();
  }

  /**
   * Show free plan interception dialog
   *
   * Displays modal to encourage user to try paid trial instead of free plan.
   * Tracks analytics event when dialog is shown.
   */
  public showFreePlanInterceptionDialog(): void {
    this.showInterceptionDialog = true;

    // Track interception shown (non-blocking)
    try {
      if (this.pricingTracker) {
        this.pricingTracker.trackFreePlanInterceptionShown();
      }
    } catch (trackingError) {
      console.warn('[OnboardPricing] Free plan interception tracking failed:', trackingError);
    }
  }

  /**
   * Handle user choosing trial from interception dialog
   *
   * Closes dialog and submits premium package with 14-day trial.
   */
  public async handleInterceptionChooseTrial(): Promise<void> {
    this.showInterceptionDialog = false;

    // Track conversion from free to trial (non-blocking)
    try {
      if (this.pricingTracker) {
        this.pricingTracker.trackFreePlanInterceptionConverted('trial');
      }
    } catch (trackingError) {
      console.warn('[OnboardPricing] Trial conversion tracking failed:', trackingError);
    }

    // Submit premium package with trial
    await this.submitPremiumPackage();
  }

  /**
   * Handle user insisting on free plan from interception dialog
   *
   * Closes dialog and proceeds with free package creation.
   */
  public async handleInterceptionChooseFree(): Promise<void> {
    this.showInterceptionDialog = false;

    // Track user chose free after seeing trial offer (non-blocking)
    try {
      if (this.pricingTracker) {
        this.pricingTracker.trackFreePlanInterceptionConverted('free');
      }
    } catch (trackingError) {
      console.warn('[OnboardPricing] Free plan conversion tracking failed:', trackingError);
    }

    // Submit free package directly
    if (this.freeBillingPackage && this.freeBillingPackage.id) {
      await this.submitPackage(this.freeBillingPackage, 0);
    }
  }

  /**
   * Handle user dismissing interception dialog
   *
   * Tracks dismissal event and closes dialog without action.
   */
  public handleInterceptionDismiss(): void {
    this.showInterceptionDialog = false;

    // Track dialog dismissed (non-blocking)
    try {
      if (this.pricingTracker) {
        this.pricingTracker.trackFreePlanInterceptionDismissed();
      }
    } catch (trackingError) {
      console.warn('[OnboardPricing] Interception dismissal tracking failed:', trackingError);
    }
  }

  /**
   * Submit the highest-priced package (Replenishment Center)
   *
   * Called by section CTA buttons throughout the pricing page to automatically
   * select and submit the premium package (highest price). This provides a quick
   * conversion path without requiring users to manually choose a plan.
   *
   * @async
   * @returns {Promise<void>}
   *
   * @remarks
   * - Gets first item from paidBillingPackageList (pre-sorted by price descending)
   * - Includes safety check for empty package list
   * - Falls back to scrolling to pricing cards if no packages available
   * - Submits with 14-day trial by default
   * - Automatically applies first-month discount if eligible (via submitPackage)
   *
   * @example
   * ```vue
   * <v-btn @click="submitPremiumPackage">Get Started</v-btn>
   * ```
   *
   * @see submitPackage - Handles actual package submission with discount logic
   * @see paidBillingPackageList - Vuex getter that returns packages sorted by price
   */
  public async submitPremiumPackage() {
    // Check if paidBillingPackageList has items
    if (!this.paidBillingPackageList.items || this.paidBillingPackageList.items.length === 0) {
      console.warn('[OnboardPricing] No paid packages available');
      // Fallback: scroll to pricing cards so user can manually select
      this.scrollToPricingCards();
      return;
    }

    // Get first package (highest price) from the sorted list
    const premiumPackage = this.paidBillingPackageList.items[0];

    await this.submitPackage(premiumPackage, 14);
  }

  // Methods for feature comparison table
  public openFeatureComparison() {
    if (this.$refs.featureComparisonTable) {
      (this.$refs.featureComparisonTable as any).showFullComparisonDialog = true;
    }
  }
  
  // Add computed property to check if we should show feature comparison
  get shouldShowFeatureComparison(): boolean {
    // Count the number of billing packages with price > 0
    const paidPackagesCount = this.filteredBillingPackages.items.filter(item => item.price > 0).length;
    // Show feature comparison only if there are at least 2 paid plans
    return paidPackagesCount >= 2;
  }

  get discountLabel(): string {
    return discountLabelFn(this.currentDiscount as ActiveDiscount | null);
  }

  get discountTrialDays(): number {
    if (this.currentDiscount?.package_config?.trialDays) {
      return this.currentDiscount.package_config.trialDays;
    }
    return 14;
  }

  /**
   * Scroll to pricing cards section with smooth animation
   *
   * Provides fallback navigation when automatic package submission fails
   * (e.g., no paid packages available). Ensures users can always access
   * the pricing cards even when CTA shortcuts don't work.
   *
   * @returns {void}
   *
   * @remarks
   * - Uses smooth scrolling for better UX
   * - Scrolls to 'start' position (top of pricing cards)
   * - Silently fails if .pricing-cards element not found
   * - Called by submitPremiumPackage when package list is empty
   *
   * @example
   * ```typescript
   * // Fallback when auto-submit fails
   * if (!packages.length) {
   *   this.scrollToPricingCards();
   * }
   * ```
   */
  public scrollToPricingCards() {
    const pricingSection = document.querySelector('.pricing-cards');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Handle ROI calculator interaction
   *
   * Tracks when user adjusts calculator values to see ROI projections.
   * Helps measure calculator engagement and its impact on conversions.
   *
   * @param calculationData - Current ROI calculation values
   */
  public handleROICalculatorInteraction(calculationData: any): void {
    // Track calculator interaction (non-blocking)
    try {
      if (this.pricingTracker) {
        this.pricingTracker.trackROICalculatorInteraction(calculationData);
      }
    } catch (trackingError) {
      // Silently fail - never block calculator UX
      console.warn('[OnboardPricing] ROI calculator tracking failed:', trackingError);
    }
  }
}
